import React, { useState, useEffect, useRef } from 'react';
import {
  getUserActivity,
  UserActivity,
} from '../../../services/getUserActivity.service';
import { authService } from '../../../services/auth.service';
import { formatLoginTime } from '../../../utils/helpers/date.helpers';
import { getErrorMessage } from '../../../utils/helpers/getErrorMessage';
import styles from './UserActivity.module.css';

interface UserActivityProps {
  className?: string;
}

const UserActivityComponent: React.FC<UserActivityProps> = ({ className }) => {
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [userActivityLoading, setUserActivityLoading] = useState(true);
  const [userActivityError, setUserActivityError] = useState<string | null>(
    null,
  );
  const [userActivityPageInfo, setUserActivityPageInfo] = useState<any>(null);
  const [userActivityHasNextPage, setUserActivityHasNextPage] = useState(false);
  const [userActivityEndCursor, setUserActivityEndCursor] = useState<
    string | null
  >(null);
  const [userActivityLoadingMore, setUserActivityLoadingMore] = useState(false);
  const activityListRef = useRef<HTMLDivElement>(null);

  const currentUser = authService.getStoredUser();

  useEffect(() => {
    if (currentUser) {
      setUserActivityLoading(true);
      setUserActivityError(null);
      getUserActivity(currentUser.id, 10)
        .then((res) => {
          setUserActivity(res.activities);
          setUserActivityPageInfo(res.pageInfo);
          setUserActivityHasNextPage(res.pageInfo?.hasNextPage || false);
          setUserActivityEndCursor(res.pageInfo?.endCursor || null);
        })
        .catch((err) => {
          setUserActivity([]);
          setUserActivityError(getErrorMessage(err));
          setUserActivityHasNextPage(false);
          setUserActivityEndCursor(null);
        })
        .finally(() => setUserActivityLoading(false));
    }
  }, [currentUser]);

  // Load more user activity (pagination)
  const loadMoreUserActivity = async () => {
    if (!currentUser || !userActivityHasNextPage || userActivityLoadingMore)
      return;
    setUserActivityLoadingMore(true);
    try {
      const res = await getUserActivity(
        currentUser.id,
        10,
        userActivityEndCursor || undefined,
      );
      setUserActivity((prev) => [...prev, ...(res.activities || [])]);
      setUserActivityHasNextPage(res.pageInfo?.hasNextPage || false);
      setUserActivityEndCursor(res.pageInfo?.endCursor || null);
    } catch (err) {
      setUserActivityError(getErrorMessage(err));
    } finally {
      setUserActivityLoadingMore(false);
    }
  };

  // Scroll handler for activity list
  const handleUserActivityScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      userActivityHasNextPage &&
      !userActivityLoadingMore &&
      !userActivityLoading
    ) {
      loadMoreUserActivity();
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className={`${styles.userActivityContainer} ${className || ''}`}>
      <div className={styles.activityCard}>
        <div className={styles.activityTitle}>User Activity</div>
        <div
          className={styles.activityList}
          ref={activityListRef}
          onScroll={handleUserActivityScroll}
          style={{ position: 'relative' }}
        >
          {userActivityLoading ? (
            <div className={styles.loadingText}>Loading...</div>
          ) : userActivityError ? (
            <div className={styles.error}>{userActivityError}</div>
          ) : userActivity.length === 0 ? (
            <div className={styles.noActivityText}>No activity found</div>
          ) : (
            <>
              {userActivity.map((a, i) => (
                <div key={i} className={styles.activityItem}>
                  <div className={styles.activityTime}>
                    {formatLoginTime(a.date)}
                  </div>
                  <div className={styles.activityDesc}>{a.activity}</div>
                </div>
              ))}
              {userActivityLoadingMore && (
                <div className={styles.loadingMoreText}>Loading more...</div>
              )}
              {!userActivityHasNextPage && userActivity.length > 0 && (
                <div className={styles.noMoreActivityText}>
                  No more activity
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserActivityComponent;
